import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    filter,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { Contact, ContactType, ContactWithPermissions } from './contacts.types';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import {
    AddInternalContactDTO,
    AddressDTO,
    ExternalContactDTO,
    ExternalContactPermissonDTO,
    InternalContactDTO,
    InternalContactPermissonDTO,
    TypeOfContact,
} from 'app/modules/shared/models/contact';

interface FoundAlphaUserDTO {
    userId: number;
    firstName: string;
    lastName: string;
    email?: string;
}

@Injectable({ providedIn: 'root' })
export class ContactsService {
    private readonly baseUrl = environment.baseUrl;

    // Private
    private _contact: BehaviorSubject<Contact | null> = new BehaviorSubject(
        null,
    );
    private _contacts: BehaviorSubject<Contact[] | null> = new BehaviorSubject(
        null,
    );

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for contact
     */
    get contact$(): Observable<Contact> {
        return this._contact.asObservable();
    }

    /**
     * Getter for contacts
     */
    get contacts$(): Observable<Contact[]> {
        return this._contacts.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get contacts
     */
    getContacts(): Observable<Contact[]> {
        return this._userService.activeUser$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .get<
                        Contact[]
                    >(`${this.baseUrl}/api/user/${user.userId}/all-contacts`)
                    .pipe(
                        map((contacts) => contacts.map(this.contactDTOMapping)),
                        tap((contacts) => {
                            // console.log(contacts);
                            contacts.sort((a, b) =>
                                a.firstName.localeCompare(b.firstName),
                            );
                            this._contacts.next(contacts);
                        }),
                    ),
            ),
        );

        // Clone the contacts
        // const contacts: Contact[] = cloneDeep(contactData);

        // Sort the contacts by the name field by default
        // contacts.sort((a, b) => a.firstName.localeCompare(b.firstName));
        // this._contacts.next(contacts);
        // return of(contacts);
    }

    /**
     * Get contacts
     */
    getContactsByUser(userId): Observable<Contact[]> {
        return this._httpClient
            .get<Contact[]>(`${this.baseUrl}/api/user/${userId}/all-contacts`)
            .pipe
            // map((contacts) => contacts.map(this.contactDTOMapping))
            // tap((contacts) => {
            //     console.log(contacts);
            //     this._contacts.next(contacts);
            // })
            ();
    }

    // All Emergency Contacts:
    // user/\{userId}/all-emergency-contacts
    getEmergencyContacts(): Observable<any> {
        return this._userService.activeUser$.pipe(
            switchMap((user) =>
                this._httpClient
                    .get<any>(
                        `${this.baseUrl}/api/user/${user.userId}/all-emergency-contacts`,
                    )
                    .pipe(
                        map((contacts) => contacts.map(this.contactDTOMapping)),
                        tap((contacts) => {
                            // console.log(contacts);
                            contacts.sort((a, b) =>
                                a.firstName.localeCompare(b.firstName),
                            );
                            this._contacts.next(contacts);
                        }),
                    ),
            ),
        );
    }

    // All Key Contacts:
    // user/{userId}/all-key-contacts
    getKeyContacts(): Observable<any> {
        return this._userService.activeUser$.pipe(
            // take(1),
            switchMap((user) =>
                this._httpClient
                    .get<any>(
                        `${this.baseUrl}/api/user/${user.userId}/all-key-contacts`,
                    )
                    .pipe(
                        map((contacts) => contacts.map(this.contactDTOMapping)),
                        tap((contacts) => {
                            // console.log(contacts);
                            contacts.sort((a, b) =>
                                a.firstName.localeCompare(b.firstName),
                            );
                            this._contacts.next(contacts);
                        }),
                    ),
            ),
        );
    }

    // all-internal-contacts
    getInternalContacts(): Observable<ContactWithPermissions[]> {
        return this._userService.activeUser$.pipe(
            switchMap((user) =>
                this._httpClient
                    .get<
                        ContactWithPermissions[]
                    >(`${this.baseUrl}/api/user/${user.userId}/all-internal-contacts`)
                    .pipe(
                        map((contacts) =>
                            contacts.map(this.contactWithPermissionsMapping),
                        ),
                        tap((contacts) => {
                            // console.log(contacts);
                            contacts.sort((a, b) =>
                                a.firstName.localeCompare(b.firstName),
                            );
                            this._contacts.next(contacts);
                        }),
                    ),
            ),
        );
    }

    contactWithPermissionsMapping(_contact) {
        const contact: ContactWithPermissions = new ContactWithPermissions();
        contact.id = _contact.id;
        contact.contactType = ContactType[_contact.contactType];
        contact.idType = _contact.idType;
        contact.firstName = _contact.firstName || '';
        contact.lastName = _contact.lastName || '';
        contact.nickName = _contact.nickName || '';
        contact.title = _contact.title;
        contact.phoneHome = _contact.phoneHome;
        contact.phoneMobile = _contact.phoneMobile;
        contact.email = _contact.email;
        contact.maySendAndReceivePhoneCalls =
            _contact.maySendAndReceivePhoneCalls;
        contact.mayUseDropInCalls = _contact.mayUseDropInCalls;
        return contact;
    }

    contactDTOMapping(_contact) {
        const contact: Contact = new Contact();
        contact.id = _contact.id;
        contact.contactType = ContactType[_contact.contactType];
        contact.idType = _contact.idType;
        contact.firstName = _contact.firstName || '';
        contact.lastName = _contact.lastName || '';
        contact.nickName = _contact.nickName || '';
        contact.title = _contact.title;
        contact.phoneHome = _contact.phoneHome;
        contact.phoneMobile = _contact.phoneMobile;
        contact.email = _contact.email;
        if (_contact.address) {
            contact.address = new AddressDTO();
            contact.address.street = _contact.address.street;
            contact.address.streetNumber = _contact.address.streetNumber;
            contact.address.zip = _contact.address.zip;
            contact.address.city = _contact.address.city;
        }
        return contact;
    }

    /**
     * Search contacts with given query
     *
     * @param query
     */
    // searchContacts(query: string): Observable<Contact[]> {
    //     return this._httpClient
    //         .get<Contact[]>('api/apps/contacts/search', {
    //             params: { query },
    //         })
    //         .pipe(
    //             tap((contacts) => {
    //                 this._contacts.next(contacts);
    //             })
    //         );
    // }

    // reset contact
    resetContact() {
        this._contact.next(null);
    }

    /**
     * Get contact by id
     */
    getInternalContactPermissions(contact: Contact): Observable<any> {
        return this._userService.activeUser$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .get<
                        ExternalContactDTO[]
                    >(`${this.baseUrl}/api/user/${user.userId}/contacts/${contact.id}/permissions`)
                    .pipe(
                        catchError((error) => {
                            if (error.status === 404) {
                                const permissions =
                                    new InternalContactPermissonDTO();
                                return of(permissions);
                            }
                        }),
                    ),
            ),
        );
    }

    /**
     * Get contact by id
     */
    getExternalContactPermissions(contact: Contact): Observable<any> {
        return this._userService.activeUser$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .get<
                        ExternalContactDTO[]
                    >(`${this.baseUrl}/api/user/${user.userId}/external-contacts/${contact.id}/permissions`)
                    .pipe(
                        catchError((error) => {
                            if (error.status === 404) {
                                const permissions =
                                    new InternalContactPermissonDTO();
                                return of(permissions);
                            }
                        }),
                    ),
            ),
        );
    }

    /**
     * Get contact by id
     */
    getContactById(id: string): Observable<Contact> {
        return this._contacts.pipe(
            take(1),
            map((contacts) => {
                // Find the contact
                const contact =
                    contacts.find((item) => item.id == Number(id)) || null;

                // // Update the contact
                // this._contact.next(contact);

                // // Return the contact
                return contact;
            }),
            switchMap((contact) => {
                // if (contact.contactType === ContactType.EXTERNAL_CONTACT) {
                //     return this.getExternalContact(id);
                // } else if (contact.contactType === ContactType.CARE_ASSISTANT) {
                //     return this.getCareTakersContact(id);
                // } else if (
                //     contact.contactType === ContactType.INTERNAL_CONTACT
                // ) {
                //     return this.getInternalContact(id);
                // }

                // if (contact.contactType === ContactType.EXTERNAL_CONTACT) {
                //     return this.getExternalContact(id);
                // } else {
                //     return this.getInternalContact(contact);
                // }

                if (contact.contactType === ContactType.EXTERNAL_CONTACT) {
                    return this.getExternalContactAndPermission(contact);
                } else {
                    return this.getInternalContactAndPermission(contact);
                }

                // if (!contact) {
                //     return throwError(
                //         'Could not found contact with id of ' + id + '!'
                //     );
                // }

                // return of(contact);
            }),
        );
    }

    /**
     * Get external contact by id
     */
    getExternalContact(externalContactId): Observable<Contact> {
        return this._userService.activeUser$.pipe(
            take(1),
            switchMap((user) =>
                this._httpClient
                    .get<ExternalContactDTO>(
                        `${this.baseUrl}/api/user/${user.userId}/external-contacts/${externalContactId}`,
                    )
                    .pipe(
                        take(1),
                        map((_contact) => {
                            const contact: Contact = _contact;
                            contact.id = externalContactId;
                            contact.firstName = _contact.firstname;
                            contact.lastName = _contact.lastname;
                            contact.nickName = _contact.nickname;
                            contact.contactType = ContactType.EXTERNAL_CONTACT;

                            // Update the contact
                            this._contact.next(contact);

                            // Return the contact
                            return contact;
                        }),
                        switchMap((contact) => {
                            if (!contact) {
                                return throwError(
                                    'Could not found contact with id of ' +
                                        user.userId +
                                        '!',
                                );
                            }

                            return of(contact);
                        }),
                    ),
            ),
        );
    }

    /**
     * Get contact by id
     */
    getInternalContact(_contactData: Contact): Observable<Contact> {
        return this._userService.activeUser$.pipe(
            take(1),
            switchMap((user) => {
                let a1 = this._httpClient.get<InternalContactDTO>(
                    `${this.baseUrl}/api/user/${_contactData.id}/base-data`,
                );
                let a2 = this.getInternalContactPermissions(_contactData);
                return forkJoin(a1, a2).pipe(
                    take(1),
                    map((data) => {
                        const contactData = data[0];
                        const permissions = data[1];

                        const contact: Contact = contactData;
                        contact.id = _contactData.id;
                        contact.contactType = _contactData.contactType;
                        contact.permissions = permissions;

                        // Update the contact
                        this._contact.next(contact);

                        // Return the contact
                        return contact;
                    }),
                    switchMap((contact) => {
                        if (!contact) {
                            return throwError(
                                'Could not found contact with id of ' +
                                    user.userId +
                                    '!',
                            );
                        }

                        return of(contact);
                    }),
                );
            }),
        );
    }

    getInternalContactAndPermission(contactData: Contact): Observable<Contact> {
        return this.getInternalContactPermissions(contactData).pipe(
            take(1),
            switchMap((permissions) => {
                const contact: Contact = contactData;
                contact.id = contactData.id;
                contact.contactType = contactData.contactType;
                contact.permissions = permissions;

                // Update the contact
                this._contact.next(contact);

                // Return the contact
                return of(contact);
            }),
        );
    }

    getExternalContactAndPermission(contactData: Contact): Observable<Contact> {
        return this.getExternalContactPermissions(contactData).pipe(
            take(1),
            switchMap((permissions) => {
                const contact: Contact = contactData;
                contact.id = contactData.id;
                contact.contactType = contactData.contactType;
                contact.permissions = permissions;

                // Update the contact
                this._contact.next(contact);

                // Return the contact
                return of(contact);
            }),
        );
    }

    /**
     * Get contact by id
     */
    getCareTakersContact(id): Observable<Contact> {
        return this._userService.activeUser$.pipe(
            take(1),
            switchMap((user) => {
                let a1 = this._httpClient.get<InternalContactDTO[]>(
                    `${this.baseUrl}/api/user/${user.userId}/care-takers/v2`,
                );
                let a2 = this.getInternalContactPermissions(id);
                return forkJoin(a1, a2).pipe(
                    take(1),
                    map((data) => {
                        const contacts = data[0];
                        const permissions = data[1];

                        // Find the contact
                        const _contact =
                            contacts.find((item) => item.userId == id) || null;
                        const contact: Contact = _contact;
                        contact.id = id;
                        contact.contactType = ContactType.CARE_ASSISTANT;
                        contact.permissions = permissions;

                        // Update the contact
                        this._contact.next(contact);

                        // Return the contact
                        return contact;
                    }),
                    switchMap((contact) => {
                        if (!contact) {
                            return throwError(
                                'Could not found contact with id of ' +
                                    user.userId +
                                    '!',
                            );
                        }

                        return of(contact);
                    }),
                );
            }),
        );
    }

    /**
     * Find User By email
     */
    findUserByEmail(email: string): Observable<FoundAlphaUserDTO[]> {
        return this._httpClient
            .post<
                FoundAlphaUserDTO[]
            >(`${this.baseUrl}/api/user/find-alpha-users-by-email`, [email])
            .pipe(tap((users) => users.map((user) => (user.email = email))));
    }

    // If not exist then add as external contact:
    /**
     * Create External Contact
     */
    saveExternalContact(
        _contact: ExternalContactDTO,
        userId: number,
    ): Observable<Contact> {
        return this.contacts$.pipe(
            take(1),
            switchMap((contacts) =>
                this._httpClient
                    .post<ExternalContactDTO>(
                        `${this.baseUrl}/api/user/${userId}/external-contacts`,
                        _contact,
                    )
                    .pipe(
                        map((newContact) => {
                            // Find the index of the updated contact
                            const index = contacts.findIndex(
                                (item) =>
                                    item.id == newContact.externalContactId,
                            );

                            const contact: Contact = _contact;
                            contact.id = newContact.externalContactId;
                            contact.contactType = ContactType.EXTERNAL_CONTACT;
                            contact.firstName = _contact.firstname;
                            contact.lastName = _contact.lastname;
                            contact.nickName = _contact.nickname;

                            if (index !== -1) {
                                // Update the contact
                                contacts[index] = contact;
                                this._contacts.next(contacts);
                            } else {
                                // Update the contacts with the new contact
                                this._contacts.next([contact, ...contacts]);
                            }

                            // Return the new contact
                            return contact;
                        }),
                    ),
            ),
        );
    }

    // If not exist then add as external contact:
    /**
     * Create Internal Contact
     */
    saveInternalContact(
        contactParams: AddInternalContactDTO,
        userId: number,
    ): Observable<any> {
        return this.contacts$.pipe(
            take(1),
            switchMap((contacts) =>
                this._httpClient
                    .post<any>(
                        `${this.baseUrl}/api/user/${userId}/contacts`,
                        contactParams,
                    )
                    .pipe(
                        map((_contact) => {
                            // Find the index of the updated contact
                            const index = contacts.findIndex(
                                (item) => item.id === _contact.userId,
                            );

                            const contact: Contact = _contact;
                            contact.id = _contact.userId;
                            contact.contactType = ContactType.INTERNAL_CONTACT;
                            if (
                                contactParams.typeOfContact ===
                                TypeOfContact.CARETAKING
                            ) {
                                contact.contactType =
                                    ContactType.CARE_ASSISTANT;
                            }

                            if (index !== -1) {
                                // Update the contact
                                contacts[index] = contact;
                                this._contacts.next(contacts);
                            } else {
                                // Update the contacts with the new contact
                                this._contacts.next([contact, ...contacts]);
                            }

                            // Return the new contact
                            return contact;
                        }),
                    ),
            ),
        );
    }

    /**
     * Update contact
     *
     * @param id
     * @param contact
     */
    setExternalContactPermissions(
        userId,
        contact: ExternalContactDTO,
    ): Observable<ExternalContactPermissonDTO> {
        return this.contacts$.pipe(
            take(1),
            switchMap((contacts) =>
                this._httpClient
                    .post<ExternalContactPermissonDTO>(
                        `${this.baseUrl}/api/user/${userId}/external-contacts/${contact.externalContactId}/permissions`,
                        contact.permissions,
                    )
                    .pipe(
                        map((updatedPermission) => {
                            // Find the index of the updated contact
                            const index = contacts
                                // .filter((c) => c.externalContactId)
                                .findIndex(
                                    (item) =>
                                        item.id == contact.externalContactId,
                                );

                            // Update the contact
                            contacts[index].permissions = contact.permissions;

                            // Update the contacts
                            this._contacts.next(contacts);

                            // Return the updated contact
                            return updatedPermission;
                        }),
                    ),
            ),
        );
    }

    /**
     * Update contact
     *
     * @param id
     * @param contact
     */
    updateContact(id: number, updatedContact: Contact): Observable<Contact> {
        return this.contacts$.pipe(
            take(1),
            map((contacts) => {
                // Find the index of the updated contact
                const index = contacts.findIndex((item) => item.id === id);

                // Update the contact
                contacts[index] = updatedContact;

                // Update the contacts
                this._contacts.next(contacts);

                // Return the updated contact
                return updatedContact;
            }),
            switchMap((updatedContact) =>
                this.contact$.pipe(
                    take(1),
                    filter((item) => item && item.id === id),
                    tap(() => {
                        // Update the contact if it's selected
                        this._contact.next(updatedContact);

                        // Return the updated contact
                        return updatedContact;
                    }),
                ),
            ),
        );
    }

    /**
     * Delete the contact
     *
     * @param id
     */
    deleteContact(userId: number, contactId: number): Observable<boolean> {
        return this.contacts$.pipe(
            take(1),
            switchMap((contacts) =>
                this._httpClient
                    .delete(
                        `${this.baseUrl}/api/user/${userId}/contacts/${contactId}`,
                    )
                    .pipe(
                        map((isDeleted: boolean) => {
                            // Find the index of the deleted contact
                            const index = contacts.findIndex(
                                (item) => item.id == contactId,
                            );

                            // Delete the contact
                            contacts.splice(index, 1);

                            // Update the contacts
                            this._contacts.next(contacts);

                            // Return the deleted status
                            return true;
                        }),
                    ),
            ),
        );
    }

    /**
     * Delete the contact
     *
     * @param contactId
     */
    deleteExternalContact(
        userId: number,
        contactId: number,
    ): Observable<boolean> {
        return this.contacts$.pipe(
            take(1),
            switchMap((contacts) =>
                this._httpClient
                    .delete(
                        `${this.baseUrl}/api/user/${userId}/external-contacts/${contactId}`,
                    )
                    .pipe(
                        map((isDeleted: boolean) => {
                            // Find the index of the deleted contact
                            const index = contacts.findIndex(
                                (item) => item.id == contactId,
                            );

                            // Delete the contact
                            contacts.splice(index, 1);

                            // Update the contacts
                            this._contacts.next(contacts);

                            // Return the deleted status
                            return true;
                        }),
                    ),
            ),
        );
    }

    /**
     * Update the avatar of the given contact
     *
     * @param id
     * @param avatar
     */
    // uploadAvatar(id: number, avatar: File): Observable<Contact> {
    //     return this.contacts$.pipe(
    //         take(1),
    //         switchMap((contacts) =>
    //             this._httpClient
    //                 .post<Contact>(
    //                     'api/apps/contacts/avatar',
    //                     {
    //                         id,
    //                         avatar,
    //                     },
    //                     {
    //                         headers: {
    //                             // eslint-disable-next-line @typescript-eslint/naming-convention
    //                             'Content-Type': avatar.type,
    //                         },
    //                     }
    //                 )
    //                 .pipe(
    //                     map((updatedContact) => {
    //                         // Find the index of the updated contact
    //                         const index = contacts.findIndex(
    //                             (item) => item.id === id
    //                         );

    //                         // Update the contact
    //                         contacts[index] = updatedContact;

    //                         // Update the contacts
    //                         this._contacts.next(contacts);

    //                         // Return the updated contact
    //                         return updatedContact;
    //                     }),
    //                     switchMap((updatedContact) =>
    //                         this.contact$.pipe(
    //                             take(1),
    //                             filter((item) => item && item.id === id),
    //                             tap(() => {
    //                                 // Update the contact if it's selected
    //                                 this._contact.next(updatedContact);

    //                                 // Return the updated contact
    //                                 return updatedContact;
    //                             })
    //                         )
    //                     )
    //                 )
    //         )
    //     );
    // }
}
